<script lang="ts" setup>
import AppAvatar from '@/components/atoms/avatar/AppAvatar.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, type Component, watch } from 'vue'

import {
  BatteryFull,
  Copy,
  File,
  Gift,
  Image as ImageIcon,
  Phone,
  SquareArrowOutUpRight,
  Wifi,
} from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import IconArrow from '@/assets/svg/arrow_back_icon.svg?component'
import { FormatHeader } from '@/services/templates/enums/format-header.enum'
import { TypeButton } from '@/services/templates/enums/type-button.enum'
import type {
  IButtonTemplate,
  ICarouselTemplate,
  IHeaderTemplate,
  ILimitedTimeOfferTemplate,
} from '@/services/templates/interfaces/template.interface'
import { formatWhatsappText } from '@/utils/whatsappFormatting'

const { t } = useI18n()

interface Props {
  header?: IHeaderTemplate
  exampleHeader?: string | null
  footer?: string
  buttons?: IButtonTemplate[]
  carousel?: ICarouselTemplate
  body: string
  bodyExamples?: string[]
  combination?: string
  category?: string
  height?: string
  width?: string
  maxHeight?: string
  maxWidth?: string
  scale?: number
  profileName?: string
  profileImage?: string | null
  profileImageComponent?: Component | null
  isCarousel?: boolean
  limitedTimeOffer?: ILimitedTimeOfferTemplate
}

const props = withDefaults(defineProps<Props>(), {
  height: 'auto',
  category: '',
  width: 'auto',
  maxHeight: 'none',
  maxWidth: 'none',
  body: '',
  bodyExamples: () => [],
  exampleHeader: null,
  combination: '',
  header: () => ({
    format: FormatHeader.NONE,
    content: '',
  }),
  footer: '',
  buttons: () => [],
  carousel: undefined,
  scale: 1,
  profileName: 'Nebula Bot',
  profileImage: null,
  profileImageComponent: null,
  isCarousel: false,
  limitedTimeOffer: () => ({
    title: '',
    hasExpirationDate: false,
    expirationDate: null,
  }),
})

const replaceIndexedVariables = (text: string, examples: string[]) =>
  text.replace(/\{\{(\d+)\}\}/g, (_match, indexValue: string) => {
    const example = String(examples[Number(indexValue) - 1] ?? '')
      .replace(/[{}]/g, '')
      .trim()
    return example
  })

const formatContent = (text: string) => {
  let content = text.trim()
  content = content.replace(/\r\n/g, '\n')
  content = formatWhatsappText(content)

  content = content.replace(
    /{{(.*?)}}/g,
    '<span class="bg-indigo-100 text-indigo-800 px-1 rounded font-mono">$1</span>',
  )
  content = content.replace(
    /^&gt; (.*)$/gm,
    '<blockquote class="border-l-4 border-gray-400 pl-4 italic text-gray-600 my-2">$1</blockquote>',
  )

  content = content.replace(/(^\d+\. .+(?:\n\d+\. .+)*)/gm, (match) => {
    const items = match
      .split('\n')
      .map((line) => line.replace(/^\d+\. (.*)/, '<li>$1</li>'))
      .join('')
    return `<ol class="list-decimal list-inside my-2">${items}</ol>`
  })

  content = content.replace(/(^- .+(?:\n- .+)*)/gm, (match) => {
    const items = match
      .split('\n')
      .map((line) => line.replace(/^- (.*)/, '<li>$1</li>'))
      .join('')
    return `<ul class="list-disc list-inside my-2">${items}</ul>`
  })

  content = content.replace(/(?<!<\/(ul|ol|li|blockquote)>)\n/g, '<br />')

  return content
}

const messageBubbles = computed(() =>
  replaceIndexedVariables(props.body, props.bodyExamples ?? [])
    .trim()
    .split(/\n{2,}/)
    .map((msg) => msg.trim())
    .filter(Boolean),
)

const headerContent = computed(() => props.header?.content?.trim() ?? '')
const isExternalMediaUrl = computed(
  () => headerContent.value.startsWith('http://') || headerContent.value.startsWith('https://'),
)
const imageLoadError = ref(false)
watch(headerContent, () => {
  imageLoadError.value = false
})
const headerVariablePattern = /\{\{1\}\}/g
const headerPreviewContent = computed(() => {
  if (props.header?.format !== FormatHeader.TEXT) return headerContent.value

  const example = String(props.exampleHeader ?? '')
    .replace(/[{}]/g, '')
    .trim()
  return headerContent.value.replace(headerVariablePattern, example)
})
const footerContent = computed(() => props.footer?.trim() ?? '')
const visibleButtons = computed(() =>
  (props.buttons ?? []).filter((button) => String(button?.text ?? '').trim()),
)
const copyCodeButton = computed(() =>
  (props.buttons ?? []).find(
    (button) => button.type === TypeButton.COPY_CODE && String(button.data ?? '').trim(),
  ),
)
const hasLimitedTimeOfferPreview = computed(
  () => props.combination === 'marketing_lto' && !props.isCarousel,
)
const showFooter = computed(() => !props.isCarousel && props.combination !== 'marketing_lto')
const limitedTimeOfferTitle = computed(() => {
  const title = props.limitedTimeOffer?.title?.trim()
  return title || t('template.preview.limited_time_offer_title')
})
const limitedTimeOfferExpirationLabel = computed(() => {
  if (!props.limitedTimeOffer?.hasExpirationDate || !props.limitedTimeOffer?.expirationDate) {
    return ''
  }

  const [year, month, day] = props.limitedTimeOffer.expirationDate.split('-').map(Number)
  if (!year || !month || !day) return ''

  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'medium',
  }).format(new Date(year, month - 1, day))
})
const limitedTimeOfferCode = computed(() => String(copyCodeButton.value?.data ?? '').trim())

const normalizedCarousel = computed(() => {
  const fallbackButtonTypes = [TypeButton.QUICK_REPLY]
  const buttonTypes = props.carousel?.buttonTypes?.length
    ? props.carousel.buttonTypes
    : fallbackButtonTypes

  return {
    mediaType: props.carousel?.mediaType ?? FormatHeader.IMAGE,
    cards: props.carousel?.cards?.length
      ? props.carousel.cards.map((card) => ({
          media: card.media ?? '',
          body: card.body ?? '',
          bodyExamples: card.bodyExamples ?? [],
          previewBody: replaceIndexedVariables(card.body ?? '', card.bodyExamples ?? []),
          buttons: buttonTypes.map((type, index) => ({
            type,
            text: card.buttons?.[index]?.text ?? '',
            data: card.buttons?.[index]?.data ?? '',
          })),
        }))
      : [
          {
            media: '',
            body: '',
            bodyExamples: [],
            previewBody: '',
            buttons: buttonTypes.map((type) => ({
              type,
              text: '',
              data: '',
            })),
          },
        ],
  }
})

const hasMessageContent = computed(() => {
  if (props.isCarousel) return Boolean(messageBubbles.value.length)
  return Boolean(
    messageBubbles.value.length ||
      headerContent.value ||
      (showFooter.value && footerContent.value) ||
      hasLimitedTimeOfferPreview.value,
  )
})

const hasPreviewContent = computed(() => {
  if (props.isCarousel) {
    return Boolean(hasMessageContent.value || normalizedCarousel.value.cards.length)
  }
  return Boolean(hasMessageContent.value || visibleButtons.value.length)
})

const documentPreviewName = computed(() => {
  const content = headerContent.value
  if (!content || content.startsWith('data:')) return t('template.preview.attached_document')
  const pathSegment = content.split('?')[0]?.split('/').pop() ?? ''
  return pathSegment || t('template.preview.attached_document')
})

const documentPreviewExtension = computed(() => {
  const extension = documentPreviewName.value.split('.').pop()?.trim().toUpperCase()
  return extension && extension !== documentPreviewName.value.toUpperCase() ? extension : 'DOC'
})

const getButtonLabel = (button: IButtonTemplate) => {
  const buttonTypeLabel: Record<TypeButton, string> = {
    [TypeButton.QUICK_REPLY]: t('template.preview.button_types.quick_reply'),
    [TypeButton.URL]: t('template.preview.button_types.url'),
    [TypeButton.COPY_CODE]: t('template.preview.button_types.copy_code'),
    [TypeButton.FLOW]: t('template.preview.button_types.flow'),
    [TypeButton.CATALOG]: t('template.preview.button_types.catalog'),
    [TypeButton.MULTI_PRODUCT]: t('template.preview.button_types.multi_product'),
    [TypeButton.ORDER_DETAILS]: t('template.preview.button_types.order_details'),
    [TypeButton.VOICE_CALL]: t('template.preview.button_types.voice_call'),
    [TypeButton.PHONE_NUMBER]: t('template.preview.button_types.voice_call'),
  }

  return button.text?.trim() || buttonTypeLabel[button.type] || t('template.preview.button')
}

const longestMessageLine = computed(() => {
  if (props.isCarousel) {
    return (
      messageBubbles.value
        .flatMap((segment) => segment.split('\n'))
        .map((segment) => segment.trim())
        .reduce((longest, segment) => (segment.length > longest.length ? segment : longest), '') ||
      ''
    )
  }

  const segments = [
    ...(props.header?.format === FormatHeader.TEXT ? [headerPreviewContent.value] : []),
    ...(hasLimitedTimeOfferPreview.value
      ? [
          limitedTimeOfferTitle.value,
          limitedTimeOfferExpirationLabel.value,
          limitedTimeOfferCode.value,
        ]
      : []),
    ...messageBubbles.value,
    ...(showFooter.value ? [footerContent.value] : []),
  ]

  return (
    segments
      .flatMap((segment) => segment.split('\n'))
      .map((segment) => segment.trim())
      .reduce((longest, segment) => (segment.length > longest.length ? segment : longest), '') || ''
  )
})

const longestButtonLabel = computed(() => {
  if (props.isCarousel) {
    return (
      normalizedCarousel.value.cards
        .flatMap((card) => card.buttons)
        .reduce((longest, button) => {
          const label = getButtonLabel(button)
          return label.length > longest.length ? label : longest
        }, '') || t('template.preview.button')
    )
  }

  return (
    visibleButtons.value.reduce((longest, button) => {
      const label = getButtonLabel(button)
      return label.length > longest.length ? label : longest
    }, '') || t('template.preview.button')
  )
})

const getButtonShapeClass = (index: number, total: number) => {
  if (total <= 1) return 'rounded-t-none rounded-b-md'
  if (index === total - 1) return 'rounded-t-none rounded-b-md'
  return 'rounded-none'
}

const messageBubbleShapeClass = computed(() =>
  !props.isCarousel && visibleButtons.value.length ? 'rounded-t-md rounded-b-none' : 'rounded-md',
)

const messageBubbleRef = ref<HTMLElement | null>(null)
const messageTextMeasureRef = ref<HTMLElement | null>(null)
const buttonTextMeasureRef = ref<HTMLElement | null>(null)
const sharedPreviewWidth = ref<number | null>(null)
const buttonsMinWidth = ref(0)
const messageMinWidth = ref(0)
const BASE_PREVIEW_MIN_WIDTH = 128
let messageResizeObserver: ResizeObserver | null = null

const stopMessageObserver = () => {
  if (!messageResizeObserver) return
  messageResizeObserver.disconnect()
  messageResizeObserver = null
}

const updateButtonsMinWidth = () => {
  const textWidth = buttonTextMeasureRef.value?.offsetWidth ?? 0
  const iconAndGapWidth = 20
  const horizontalPadding = 32
  buttonsMinWidth.value = Math.max(
    BASE_PREVIEW_MIN_WIDTH,
    textWidth + iconAndGapWidth + horizontalPadding,
  )
}

const updateMessageMinWidth = () => {
  const textWidth = messageTextMeasureRef.value?.offsetWidth ?? 0
  const horizontalPadding = 28
  messageMinWidth.value = Math.max(BASE_PREVIEW_MIN_WIDTH, textWidth + horizontalPadding)
}

const updateSharedPreviewWidth = () => {
  const messageWidth = messageBubbleRef.value?.offsetWidth ?? 0
  const minWidth = Math.max(
    buttonsMinWidth.value || BASE_PREVIEW_MIN_WIDTH,
    messageMinWidth.value || BASE_PREVIEW_MIN_WIDTH,
  )

  if (messageWidth) {
    const nextWidth = Math.max(messageWidth, minWidth)
    if (sharedPreviewWidth.value !== nextWidth) sharedPreviewWidth.value = nextWidth
    return
  }

  if (visibleButtons.value.length) {
    if (sharedPreviewWidth.value !== minWidth) sharedPreviewWidth.value = minWidth
    return
  }

  if (sharedPreviewWidth.value !== null) sharedPreviewWidth.value = null
}

const startMessageObserver = () => {
  stopMessageObserver()
  if (!messageBubbleRef.value) return
  if (typeof ResizeObserver === 'undefined') return

  messageResizeObserver = new ResizeObserver(() => {
    updateSharedPreviewWidth()
  })
  messageResizeObserver.observe(messageBubbleRef.value)
}

const sharedPreviewStyle = computed(() => {
  const minWidth = Math.max(
    buttonsMinWidth.value || BASE_PREVIEW_MIN_WIDTH,
    messageMinWidth.value || BASE_PREVIEW_MIN_WIDTH,
  )

  if (!sharedPreviewWidth.value) {
    return {
      width: `min(${minWidth}px, 90%)`,
      maxWidth: '90%',
    }
  }

  return {
    width: `min(${sharedPreviewWidth.value}px, 90%)`,
    maxWidth: '90%',
  }
})

const basePhoneWidth = 12
const basePhoneHeight = 22

const phoneWidth = computed(() => {
  return props.width !== 'auto' ? props.width : `${basePhoneWidth * props.scale}rem`
})

const phoneHeight = computed(() => {
  return props.height !== 'auto' ? props.height : `${basePhoneHeight * props.scale}rem`
})

const previewTime = computed(() =>
  new Intl.DateTimeFormat('es-CO', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date()),
)

watch(
  () => [props.body, props.header, props.footer, props.buttons, props.carousel, props.isCarousel],
  async () => {
    await nextTick()
    updateMessageMinWidth()
    updateButtonsMinWidth()
    updateSharedPreviewWidth()
    startMessageObserver()
  },
  { deep: true },
)

onMounted(async () => {
  await nextTick()
  updateMessageMinWidth()
  updateButtonsMinWidth()
  updateSharedPreviewWidth()
  startMessageObserver()
})

onBeforeUnmount(() => {
  stopMessageObserver()
})
</script>

<template>
  <div class="flex justify-center w-fit mx-auto lg:justify-normal px-4 pt-0 pb-0">
    <div
      class="relative bg-gradient-to-b from-[var(--p-primary-300)] to-[var(--p-primary-400)] border border-[var(--p-primary-500)] rounded-[2.5rem] flex items-center justify-center box-border shadow-xl"
      :style="{
        width: phoneWidth,
        height: phoneHeight,
        padding: `${0.15 * scale}rem`,
        maxHeight: props.maxHeight,
        maxWidth: props.maxWidth,
      }"
    >
      <div
        class="absolute -right-1 top-[30%] w-1 h-16 rounded-r-sm bg-gradient-to-r from-neutral-400 via-[var(--p-primary-500)] to-[var(--p-primary-500)] border-r border-[var(--p-primary-color)] shadow-[1px_0_1px_rgba(0,0,0,0.2)_inset]"
      ></div>

      <div
        v-for="(button, index) in [
          { top: '38%', height: '2rem' },
          { top: '28%', height: '2rem' },
          { top: '20%', height: '1rem' },
        ]"
        :key="index"
        class="absolute -left-1 w-1 rounded-r-sm bg-gradient-to-l from-neutral-400 via-[var(--p-primary-500)] to-[var(--p-primary-500)] border-l border-primary-200 shadow-[1px_0_1px_rgba(0,0,0,0.2)_inset]"
        :style="{
          top: button.top,
          height: button.height,
        }"
      ></div>

      <div
        class="h-full w-full bg-black rounded-[2.4rem] flex items-center justify-center box-border p-1"
      >
        <div
          class="h-full w-full rounded-[2rem] flex flex-col overflow-hidden bg-[image:var(--bg-whatapp)] bg-[length:150%_150%] bg-center bg-no-repeat dark:bg-[image:var(--bg-whatapp-dark)]"
        >
          <div
            class="flex flex-col items-center gap-2 bg-[var(--whatsapp-header-green)] px-2 py-1 shadow-xs dark:!bg-[var(--whatsapp-header-dark-green)] dark:text-white"
          >
            <div class="relative w-full pt-1">
              <div
                class="flex sm:w-15 2xl:w-18 h-1.5 bg-black absolute left-1/2 -translate-x-1/2 top-[0.3rem] rounded-full"
              ></div>
              <div class="flex h-2 w-full items-center justify-between px-3">
                <div class="text-[8px] font-regular leading-none text-white/90">
                  {{ previewTime }}
                </div>
                <div class="flex items-center gap-1">
                  <Wifi :size="10" class="text-white/90"></Wifi>
                  <BatteryFull :size="10" class="text-white/90"></BatteryFull>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-2 w-full px-1">
              <div
                v-if="profileImageComponent"
                class="shrink-0 h-6 w-6 overflow-hidden rounded-full bg-white/20 [&_*]:h-full [&_*]:w-full [&_img]:object-cover"
              >
                <component :is="profileImageComponent" />
              </div>
              <AppAvatar
                v-else
                :image="profileImage"
                :label="profileImage ? '' : profileName.charAt(0).toUpperCase()"
                shape="circle"
                class="shrink-0 [&_.p-avatar]:!h-6 [&_.p-avatar]:!w-6 [&_.p-avatar]:!text-[8px] [&_img]:h-full [&_img]:w-full [&_img]:object-cover"
              />
              <div class="min-w-0 leading-tight">
                <p class="truncate text-[10px] font-medium text-white/90">{{ profileName }}</p>
                <p class="truncate text-[8px] text-white/60">en linea</p>
              </div>
            </div>
          </div>

          <div
            class="w-full h-full p-2 custom-scroll overflow-y-auto overflow-x-hidden break-words"
          >
            <div v-if="hasPreviewContent">
              <div v-if="hasMessageContent" class="flex justify-start">
                <div
                  ref="messageBubbleRef"
                  class="relative w-fit max-w-[90%] bg-white px-2 py-2 text-black dark:bg-[var(--whatsapp-dark)] dark:text-white"
                  :class="messageBubbleShapeClass"
                  :style="sharedPreviewStyle"
                >
                  <span
                    aria-hidden="true"
                    class="absolute left-0 top-0 h-3 w-3 -translate-x-[35%] rounded-tl-[0.2rem] bg-white [clip-path:polygon(100%_0,100%_100%,0_0)] dark:bg-[#202c33]"
                  />

                  <template v-if="!props.isCarousel">
                    <p
                      v-if="props.header.format === FormatHeader.TEXT && headerContent"
                      class="mb-1 text-[9px] font-semibold leading-tight whitespace-pre-wrap break-words"
                    >
                      {{ headerPreviewContent }}
                    </p>

                    <div
                      v-if="props.header.format === FormatHeader.IMAGE && headerContent"
                      class="mb-2"
                    >
                      <img
                        v-if="!imageLoadError"
                        :src="headerContent"
                        :alt="t('template.preview.header_preview_alt')"
                        class="mx-auto max-h-36 max-w-full object-contain rounded-md border border-black/10 dark:border-white/10"
                        @error="imageLoadError = true"
                      />
                      <div
                        v-else
                        class="w-full max-h-36 h-20 rounded-md border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/10 flex items-center justify-center"
                      >
                        <ImageIcon :size="28" class="text-black/30 dark:text-white/30" />
                      </div>
                    </div>

                    <div
                      v-if="props.header.format === FormatHeader.VIDEO && headerContent"
                      class="mb-2"
                    >
                      <video
                        :src="headerContent"
                        controls
                        class="mx-auto max-h-36 max-w-full object-contain rounded-md border border-black/10 dark:border-white/10"
                      />
                    </div>

                    <div
                      v-if="props.header.format === FormatHeader.DOCUMENT && headerContent"
                      class="mb-2 rounded-md border border-black/10 bg-black/5 p-2 dark:border-white/10 dark:bg-white/10"
                    >
                      <div class="flex items-center gap-2">
                        <div
                          class="flex h-10 w-8 shrink-0 flex-col overflow-hidden rounded-md border border-[var(--p-primary-200)] bg-[var(--p-primary-50)] text-[var(--p-primary-color)] dark:border-white/10 dark:bg-white/10 dark:text-white"
                        >
                          <div class="flex flex-1 items-center justify-center">
                            <File :size="16" :stroke-width="1.7" />
                          </div>
                          <span
                            class="bg-[var(--p-primary-color)] px-0.5 py-[2px] text-center text-[7px] font-semibold leading-none text-white"
                          >
                            {{ documentPreviewExtension }}
                          </span>
                        </div>

                        <div class="min-w-0 flex-1">
                          <p class="truncate text-[8px] font-semibold leading-tight">
                            {{ documentPreviewName }}
                          </p>
                          <p
                            class="mt-0.5 text-[7px] leading-tight text-black/55 dark:text-white/65"
                          >
                            {{ t('template.format_header.document') }}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="hasLimitedTimeOfferPreview"
                      class="mb-2 rounded-md bg-[var(--p-primary-50)]/80 px-2 py-1.5 dark:bg-white/10"
                    >
                      <div class="flex items-start gap-2">
                        <div
                          class="mt-[1px] flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--p-primary-100)] text-[var(--p-primary-color)] dark:bg-white/15 dark:text-white"
                        >
                          <Gift :size="12" :stroke-width="1.8" />
                        </div>

                        <div class="min-w-0 flex-1">
                          <p class="text-[9px] font-semibold leading-tight break-words">
                            {{ limitedTimeOfferTitle }}
                          </p>
                          <p
                            v-if="limitedTimeOfferExpirationLabel"
                            class="mt-0.5 text-[8px] leading-tight text-black/65 dark:text-white/70"
                          >
                            {{
                              t('template.preview.expires_on', {
                                date: limitedTimeOfferExpirationLabel,
                              })
                            }}
                          </p>
                          <p
                            v-if="limitedTimeOfferCode"
                            class="mt-0.5 text-[8px] leading-tight text-black/65 dark:text-white/70"
                          >
                            {{ t('template.preview.code', { code: limitedTimeOfferCode }) }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </template>

                  <div
                    v-for="(msg, idx) in messageBubbles"
                    :key="idx"
                    v-html="formatContent(msg)"
                    class="prose prose-sm max-w-full text-[9px]! dark:prose-invert"
                  ></div>

                  <p
                    v-if="showFooter && footerContent"
                    class="mt-1 text-[8px] leading-tight text-black/60 whitespace-pre-wrap break-words dark:text-white/70"
                  >
                    {{ footerContent }}
                  </p>

                  <div
                    class="mt-1 flex items-center justify-end gap-1 text-[0.55rem] leading-none text-black/55 dark:text-white/70"
                  >
                    <span class="text-[8px]!">{{ previewTime }}</span>
                  </div>
                </div>
              </div>

              <template v-if="!props.isCarousel && visibleButtons.length">
                <div class="flex justify-start">
                  <div class="relative w-fit max-w-[90%] flex-col" :style="sharedPreviewStyle">
                    <button
                      v-for="(btn, index) in visibleButtons"
                      :key="index"
                      type="button"
                      class="w-full font-semibold inline-flex cursor-default items-center justify-center gap-1 mt-[1px] bg-white text-(--whatsapp-principal-green) text-[10px] px-4 py-2 dark:bg-[#202c33] whitespace-normal"
                      :class="getButtonShapeClass(index, visibleButtons.length)"
                    >
                      <IconArrow
                        v-if="btn.type === TypeButton.QUICK_REPLY"
                        width="14"
                        height="14"
                      />
                      <SquareArrowOutUpRight
                        v-else-if="btn.type === TypeButton.URL"
                        :size="12"
                        :stroke-width="1.8"
                      />
                      <Copy
                        v-else-if="btn.type === TypeButton.COPY_CODE"
                        :size="12"
                        :stroke-width="1.8"
                      />
                      <Phone
                        v-else-if="btn.type === TypeButton.VOICE_CALL"
                        :size="12"
                        :stroke-width="1.8"
                      />

                      <span
                        v-html="formatContent(getButtonLabel(btn))"
                        class="prose prose-sm min-w-0 max-w-full text-[9px]! leading-tight text-center dark:prose-invert whitespace-normal break-words"
                      ></span>
                    </button>
                  </div>
                </div>
              </template>

              <template v-if="props.isCarousel">
                <div class="mt-1 overflow-x-auto custom-scroll carousel-track pb-2">
                  <div class="flex min-w-max gap-1 pr-2">
                    <div
                      v-for="(card, cardIndex) in normalizedCarousel.cards"
                      :key="cardIndex"
                      class="w-[8.8rem] shrink-0 snap-start"
                    >
                      <div
                        class="rounded-t-xl bg-white p-1.5 text-black dark:bg-[var(--whatsapp-dark)] dark:text-white"
                      >
                        <div class="overflow-hidden rounded-lg bg-black/5 dark:bg-white/10">
                          <img
                            v-if="normalizedCarousel.mediaType === FormatHeader.IMAGE && card.media"
                            :src="card.media"
                            :alt="t('template.preview.card_preview_alt')"
                            class="h-28 w-full object-cover"
                          />
                          <video
                            v-else-if="
                              normalizedCarousel.mediaType === FormatHeader.VIDEO && card.media
                            "
                            :src="card.media"
                            controls
                            class="h-28 w-full object-cover"
                          />
                          <div
                            v-else
                            class="flex h-28 items-center justify-center bg-[linear-gradient(135deg,rgba(37,99,235,0.12),rgba(14,165,233,0.18))] text-center text-[9px] font-medium text-slate-600 dark:text-slate-200"
                          >
                            {{
                              normalizedCarousel.mediaType === FormatHeader.IMAGE
                                ? t('template.format_header.image')
                                : t('template.format_header.video')
                            }}
                          </div>
                        </div>

                        <div class="px-1.5 pb-1 pt-2">
                          <div
                            v-if="card.previewBody.trim()"
                            :key="`${cardIndex}-${card.previewBody}`"
                            v-html="formatContent(card.previewBody)"
                            class="prose prose-sm max-w-full text-[9px]! dark:prose-invert break-words"
                          ></div>
                          <p
                            v-else
                            class="text-[9px] leading-tight text-black/45 dark:text-white/45"
                          >
                            {{ t('template.preview.card_description_placeholder') }}
                          </p>

                          <div
                            class="mt-1 flex justify-end text-[8px] text-black/55 dark:text-white/70"
                          >
                            {{ previewTime }}
                          </div>
                        </div>
                      </div>

                      <div class="mt-[1px] overflow-hidden rounded-b-xl">
                        <button
                          v-for="(btn, index) in card.buttons"
                          :key="`${cardIndex}-${index}`"
                          type="button"
                          class="w-full font-semibold inline-flex cursor-default items-center justify-center gap-1 mt-[1px] bg-white text-(--whatsapp-principal-green) text-[10px] px-3 py-2 dark:bg-[var(--whatsapp-dark)] whitespace-normal"
                          :class="getButtonShapeClass(index, card.buttons.length)"
                        >
                          <IconArrow
                            v-if="btn.type === TypeButton.QUICK_REPLY"
                            width="14"
                            height="14"
                          />
                          <SquareArrowOutUpRight
                            v-else-if="btn.type === TypeButton.URL"
                            :size="12"
                            :stroke-width="1.8"
                          />
                          <Phone
                            v-else-if="btn.type === TypeButton.VOICE_CALL"
                            :size="12"
                            :stroke-width="1.8"
                          />

                          <span
                            v-html="formatContent(getButtonLabel(btn))"
                            class="prose prose-sm min-w-0 max-w-full text-[9px]! leading-tight text-center dark:prose-invert whitespace-normal break-words"
                          ></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <span
                ref="messageTextMeasureRef"
                aria-hidden="true"
                class="pointer-events-none fixed -left-[9999px] -top-[9999px] text-[9px] whitespace-nowrap"
              >
                {{ longestMessageLine }}
              </span>

              <span
                ref="buttonTextMeasureRef"
                aria-hidden="true"
                class="pointer-events-none fixed -left-[9999px] -top-[9999px] text-[10px] font-semibold whitespace-nowrap"
              >
                {{ longestButtonLabel }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-scroll::-webkit-scrollbar {
  width: 3px;
  height: 3px;
}

.custom-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background-color: var(--p-primary-500);
  border-radius: 3px;
}

.custom-scroll {
  scrollbar-width: thin;
  scrollbar-color: var(--p-primary-500) transparent;
}

.carousel-track {
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
}
</style>
